import { useRef } from 'react';
import type { Editor } from '@tiptap/react';
import { increaseIndent, decreaseIndent } from '@/lib/richTextIndent';
import { useTooltip } from '@/hooks/useTooltip';
import { showToast } from '@/lib/toast';
import { RichTextLinkButton } from './RichTextLinkButton';
import { RichTextColorButton } from './RichTextColorButton';

/**
 * Панель форматирования "Текст документа" — набор действий 1:1 старый
 * Quill-тулбар в Orchid (см. референс-скриншот в задаче на перенос
 * OperatorEditLayout): buttons вместо готового набора SVG-иконок Quill (в
 * проекте их нет) — латинские буквы-подписи сами выглядят как результат
 * действия (жирная/курсивная/подчёркнутая/зачёркнутая), а не абстрактная
 * пиктограмма. Ссылка и картинка — единственные два места, где буква не
 * читалась бы однозначно, для них простые монохромные SVG.
 */
export function RichTextToolbar({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pickImage = () => fileInputRef.current?.click();

  const onImageSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // accept="image/*" – это лишь подсказка диалогу выбора файла, браузер не
    // запрещает выбрать файл другого типа (например, через "Все файлы") –
    // проверяем MIME реально.
    if (!file.type.startsWith('image/')) {
      showToast('Можно вставить только изображение.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') editor.chain().focus().setImage({ src: reader.result }).run();
    };
    reader.readAsDataURL(file);
  };

  const marks: ToolbarButton[] = [
    { label: 'B', title: 'Жирный', active: editor.isActive('bold'), onClick: () => editor.chain().focus().toggleBold().run(), labelStyle: { fontWeight: 800 } },
    { label: 'I', title: 'Курсив', active: editor.isActive('italic'), onClick: () => editor.chain().focus().toggleItalic().run(), labelStyle: { fontStyle: 'italic' } },
    { label: 'U', title: 'Подчёркнутый', active: editor.isActive('underline'), onClick: () => editor.chain().focus().toggleUnderline().run(), labelStyle: { textDecoration: 'underline' } },
    { label: 'S', title: 'Зачёркнутый', active: editor.isActive('strike'), onClick: () => editor.chain().focus().toggleStrike().run(), labelStyle: { textDecoration: 'line-through' } },
  ];

  const afterLink: ToolbarButton[] = [
    { label: 'Tx', title: 'Очистить форматирование', active: false, onClick: () => editor.chain().focus().unsetAllMarks().clearNodes().run(), labelStyle: { textDecoration: 'line-through' } },
  ];

  const blocks: ToolbarButton[] = [
    { label: '”', title: 'Цитата', active: editor.isActive('blockquote'), onClick: () => editor.chain().focus().toggleBlockquote().run() },
    { label: '</>', title: 'Код', active: editor.isActive('codeBlock'), onClick: () => editor.chain().focus().toggleCodeBlock().run(), labelStyle: { fontFamily: 'monospace' } },
    { label: 'H1', title: 'Заголовок 1', active: editor.isActive('heading', { level: 1 }), onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
    { label: 'H2', title: 'Заголовок 2', active: editor.isActive('heading', { level: 2 }), onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { label: '1.', title: 'Нумерованный список', active: editor.isActive('orderedList'), onClick: () => editor.chain().focus().toggleOrderedList().run() },
    { label: '•', title: 'Маркированный список', active: editor.isActive('bulletList'), onClick: () => editor.chain().focus().toggleBulletList().run() },
    { label: '⇤', title: 'Уменьшить отступ', active: false, onClick: () => decreaseIndent(editor) },
    { label: '⇥', title: 'Увеличить отступ', active: false, onClick: () => increaseIndent(editor) },
  ];

  const aligns: ToolbarButton[] = [
    { icon: <AlignIcon align="left" />, title: 'По левому краю', active: editor.isActive({ textAlign: 'left' }), onClick: () => editor.chain().focus().setTextAlign('left').run() },
    { icon: <AlignIcon align="center" />, title: 'По центру', active: editor.isActive({ textAlign: 'center' }), onClick: () => editor.chain().focus().setTextAlign('center').run() },
    { icon: <AlignIcon align="right" />, title: 'По правому краю', active: editor.isActive({ textAlign: 'right' }), onClick: () => editor.chain().focus().setTextAlign('right').run() },
    { icon: <AlignIcon align="justify" />, title: 'По ширине', active: editor.isActive({ textAlign: 'justify' }), onClick: () => editor.chain().focus().setTextAlign('justify').run() },
  ];

  return (
    <div className="rich-text-editor__toolbar" role="toolbar" aria-label="Форматирование текста">
      {marks.map((b) => (
        <ToolbarBtn key={b.title} button={b} disabled={disabled} />
      ))}
      <RichTextLinkButton editor={editor} disabled={disabled} />
      {afterLink.map((b) => (
        <ToolbarBtn key={b.title} button={b} disabled={disabled} />
      ))}

      <RichTextColorButton editor={editor} disabled={disabled} mode="text" />
      <RichTextColorButton editor={editor} disabled={disabled} mode="highlight" />

      {blocks.map((b) => (
        <ToolbarBtn key={b.title} button={b} disabled={disabled} />
      ))}

      {aligns.map((b) => (
        <ToolbarBtn key={b.title} button={b} disabled={disabled} />
      ))}

      <ToolbarBtn
        button={{ icon: <ImageIcon />, title: 'Изображение', active: false, onClick: pickImage }}
        disabled={disabled}
      />
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onImageSelected} />
    </div>
  );
}

type ToolbarButton = {
  title: string;
  active: boolean;
  onClick: () => void;
  label?: string;
  icon?: React.ReactNode;
  /** Буква-подпись сама выглядит как результат действия (жирная/курсивная/…) – понятнее пиктограммы без готового набора иконок. */
  labelStyle?: React.CSSProperties;
};

/** Тултип — общий useTooltip страницы, а не нативный title (тот не стилизуется и появляется с задержкой ОС). */
function ToolbarBtn({ button, disabled }: { button: ToolbarButton; disabled: boolean }) {
  const tooltip = useTooltip<HTMLButtonElement>(button.title);
  return (
    <button
      ref={tooltip.ref}
      type="button"
      aria-label={button.title}
      aria-pressed={button.active}
      disabled={disabled}
      className={`rich-text-editor__btn${button.active ? ' rich-text-editor__btn--active' : ''}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={button.onClick}
      {...tooltip.handlers}
    >
      {button.icon ?? <span style={button.labelStyle}>{button.label}</span>}
      {tooltip.node}
    </button>
  );
}

/** Иконки выравнивания — четыре линии, у которых длина/сдвиг передают сам режим. */
function AlignIcon({ align }: { align: 'left' | 'center' | 'right' | 'justify' }) {
  const lines: Record<typeof align, [number, number][]> = {
    left: [[3, 18], [3, 12], [3, 18], [3, 12]],
    center: [[3, 18], [6, 12], [3, 18], [6, 12]],
    right: [[3, 18], [9, 12], [3, 18], [9, 12]],
    justify: [[3, 18], [3, 18], [3, 18], [3, 18]],
  };
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      {lines[align].map(([x, width], i) => (
        <line key={i} x1={x} y1={6 + i * 4} x2={x + width} y2={6 + i * 4} />
      ))}
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}
